import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const REFRESH_MS = 60_000; // refresh every minute

const ImportantNotesContext = createContext(null);

export function ImportantNotesProvider({ children }) {
    const { user } = useAuth();
    const [flags, setFlags] = useState({}); // { [prospect_id]: count }
    const timerRef = useRef(null);

    const refresh = useCallback(async () => {
        if (!user) return;
        try {
            const res = await axios.get(`${API_URL}/api/prospects-important-flags`, {
                withCredentials: true,
            });
            setFlags(res.data?.flags || {});
        } catch {
            // silently ignore - not blocking
        }
    }, [user]);

    useEffect(() => {
        if (!user) {
            setFlags({});
            return;
        }
        refresh();
        timerRef.current = setInterval(refresh, REFRESH_MS);
        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
        };
    }, [user, refresh]);

    // Allow children to optimistically update a prospect's flag without waiting
    // for the next refresh tick (after add / delete in ProspectDetail).
    const setProspectCount = useCallback((prospectId, count) => {
        if (!prospectId) return;
        setFlags(prev => {
            const next = { ...prev };
            if (count > 0) next[prospectId] = count;
            else delete next[prospectId];
            return next;
        });
    }, []);

    return (
        <ImportantNotesContext.Provider value={{ flags, refresh, setProspectCount }}>
            {children}
        </ImportantNotesContext.Provider>
    );
}

export function useImportantNotes() {
    const ctx = useContext(ImportantNotesContext);
    if (!ctx) {
        // Defensive default - lets components render even if provider is missing
        return { flags: {}, refresh: () => {}, setProspectCount: () => {} };
    }
    return ctx;
}

/**
 * Returns true if the given prospect has at least one important note.
 * Lightweight read against the in-memory flags map.
 */
export function useHasImportantNotes(prospectId) {
    const { flags } = useImportantNotes();
    if (!prospectId) return false;
    return (flags[prospectId] || 0) > 0;
}
